import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { contentApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePageviewTracker } from '../../hooks/usePageviewTracker';
import { formatDate, getErrorMessage } from '../../utils/helpers';
import { sanitizeHtml } from '../../utils/htmlSanitizer';

function ContentDetail() {
  const { contentSlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await contentApi.getContentById(contentSlug);
      setContent(response.content);
    } catch (err) {
      console.error('Failed to fetch content:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [contentSlug]);

  useEffect(() => {
    if (contentSlug) {
      fetchContent();
    }
  }, [contentSlug, fetchContent]);

  // Track pageview for this content
  usePageviewTracker({
    contentId: content?._id || content?.id,
    contentType: 'content',
    contentTypeCategory: content?.type
  });

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this content? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      await contentApi.deleteContent(content.id || content._id);
      navigate('/content');
    } catch (err) {
      console.error('Failed to delete content:', err);
      alert('Failed to delete content: ' + getErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="content-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content-error">
        <div className="error-message" role="alert">
          <h2>Error Loading Content</h2>
          <p>{error}</p>
          <Link to="/content" className="btn btn-primary">
            Back to Content List
          </Link>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="content-not-found">
        <h2>Content Not Found</h2>
        <p>The content you're looking for doesn't exist or has been removed.</p>
        <Link to="/content" className="btn btn-primary">
          Back to Content List
        </Link>
      </div>
    );
  }

  const isAuthor = isAuthenticated && user && user.id === content.authorId;
  const canEdit = isAuthor || (user && user.roles && user.roles.includes('admin'));

  return (
    <div className="content-detail">
      <div className="content-detail-header">
        <div className="header-top-row">
          <div className="breadcrumb">
            <Link to="/content">Content</Link>
            <span> / </span>
            <span>{content.title}</span>
          </div>

          {canEdit && (
            <div className="content-actions">
              <Link
                to={`/content/edit/${content.id || content._id}`}
                className="btn btn-secondary btn-xs"
              >
                Edit
              </Link>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="btn btn-danger btn-xs"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          )}
        </div>

        <div className="header-main">
          <div className="header-title-row">
            <h1 className="article-title">{content.title}</h1>
            
            <div className="article-meta-compact">
              <span className={`content-type-badge content-type-${content.type}`}>
                {content.type}
              </span>
              <span className="meta-separator">•</span>
              <span className="article-author">by {content.authorDisplayName}</span>
              <span className="meta-separator">•</span>
              <span className="article-date">
                Published {formatDate(content.publishedAt)}
              </span>
              {content.viewCount > 0 && (
                <>
                  <span className="meta-separator">•</span>
                  <span className="article-views">{content.viewCount} views</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {content.excerpt && (
        <div className="article-excerpt">
          <p>{content.excerpt}</p>
        </div>
      )}

      <article className="content-article">
        <div className="article-content">
          <div 
            className="content-body content-preview"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(content.body) }}
          />
        </div>

        {content.tags && content.tags.length > 0 && (
          <div className="article-tags-footer">
            {content.tags.map((tag, index) => (
              <span key={index} className="article-tag">
                {tag}
              </span>
            ))}
          </div>
        )}

        <footer className="article-footer">
          <div className="article-meta-footer">
            {content.updatedAt && content.updatedAt !== content.createdAt && (
              <p className="last-updated">
                Last updated: {formatDate(content.updatedAt)}
              </p>
            )}
          </div>
        </footer>
      </article>

      <div className="content-navigation">
        <Link to="/content" className="btn btn-secondary btn-lg">
          ← Back to Content List
        </Link>
      </div>
    </div>
  );
}

export default ContentDetail;